import { Link } from "react-router-dom";
import { BookOpen, CalendarPlus, NotebookPen, Target } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { coachPortalShortcuts } from "../lib/coachShortcuts";
import { markNav, track } from "../lib/usageTracker";

/**
 * Issue #460 — the Coach Portal's top ribbon, for the selected player.
 *
 * WHAT EACH BUTTON DOES lives in `lib/coachShortcuts.js`, which is where
 * the reasoning about reusing existing flows is written down and where the
 * unit test can reach it. This file is presentation: it renders that table
 * and dispatches the three action shapes.
 *
 * Extracted out of `CoachPortal.jsx` rather than added to it: that function
 * was already 833 lines against the 800-line budget in `eslint.config.mjs`
 * before this slice, so a ribbon written inline would have deepened a
 * warning the budget exists to stop growing.
 */

const NAVY = "#0B2E59";
const GOLD = "#D4A017";

// #106's phone touch-target idiom, the same pair every control on this page
// and in TrainingSessions.jsx carries.
const TOUCH = "h-11 sm:h-9";

const ICONS = {
  goal: Target,
  training: CalendarPlus,
  feedback: NotebookPen,
  guide: BookOpen,
};

export default function CoachPortalShortcuts({ playerId, onOpenGoalDialog, onGoToTab }) {
  const items = coachPortalShortcuts(playerId);

  return (
    <Card className="border-slate-200" data-testid="cp-shortcuts">
      <CardContent className="p-3 sm:p-4">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2 px-1">Shortcuts</div>
        <div className="flex flex-wrap gap-2">
          {items.map((item) => {
            const Icon = ICONS[item.key];
            const face = (
              <>
                {Icon && <Icon size={15} className="mr-1.5" />}
                {item.label}
              </>
            );

            // A DESTINATION is a real <Link>, for UpcomingAddMenu's reason:
            // ⌘-click and "open in new tab" work, the status bar shows where
            // it goes, and a screen reader announces a link. The other two
            // stay on this page, so they are buttons.
            if (item.action.type === "link") {
              return (
                <Button
                  key={item.key}
                  asChild
                  variant="outline"
                  className={`${TOUCH} px-3 border-slate-200`}
                  data-testid={item.testId}
                  // `asChild` hands this to the <Link>: marked before the navigation it causes.
                  onClick={() => {
                    markNav("coach_shortcut");
                    track("coach.shortcut", { item: item.key });
                  }}
                >
                  <Link to={item.action.to}>{face}</Link>
                </Button>
              );
            }

            const isPrimary = item.action.type === "dialog";
            return (
              <Button
                key={item.key}
                variant={isPrimary ? "default" : "outline"}
                onClick={() => {
                  track("coach.shortcut", { item: item.key });
                  if (isPrimary) onOpenGoalDialog();
                  else onGoToTab(item.action.tab, { focus: Boolean(item.action.focus) });
                }}
                className={`${TOUCH} px-3 ${isPrimary ? "text-slate-900" : "border-slate-200"}`}
                style={isPrimary ? { background: GOLD } : undefined}
                data-testid={item.testId}
              >
                {isPrimary ? face : <span className="flex items-center" style={{ color: NAVY }}>{face}</span>}
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
